import type { FC, FormEvent, ChangeEvent } from 'react';
import type { BookBaseColor, SalesValues } from 'shared/types/index';

import tinycolor from 'tinycolor2';
import { useMemo, useState, lazy } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel } from '@mui/material';

import { COLOR_BLACK_TEXT, COLOR_MEDIUM_GRAY } from 'shared/helpers/colors';
import { filterBackgroundColor } from 'shared/helpers/website';

import useSales from 'shared/hooks/useSales';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import { FlexBox } from 'shared/components/Common';
import { generateAccessKey, sanitizeRichText } from 'shared/helpers/utils';
import SalesStatesDropdown from '../Common/SalesStatesDropdown';
import { TOP_USED_CURRENCIES } from 'shared/helpers/static';
import ButtonActionModal from 'shared/components/Common/ButtonActionModal';
import ButtonSettingIcon from 'shared/components/Sales/Common/ButtonSettingIcon';

const PhoneInput = lazy(() => import('react-phone-number-input'));

const Form = styled.form`
  position: relative;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 30px;
`;

const InputLarge = styled.input`
  border-radius: 8px;
  border: 1px solid ${COLOR_MEDIUM_GRAY};
  width: 100%;
  height: ${56 + 56 * 0.38}px;
  padding: 0 25px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
`;

const InputInner = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  width: 100%;
  gap: 10px;

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    flex-direction: column;
  `}
`;

const Paragraph = styled.p`
  font-weight: 700;
  font-size: 20px;
  line-height: 2rem;
  display: flex;
  align-items: center;
  color: ${COLOR_BLACK_TEXT};

  @media only screen and (max-width: 37.5em) {
    font-size: 1.2rem;
  }
`;

const Input = styled.input`
  height: ${56 + 56 * 0.38}px;
  padding: 0 25px;
  width: 49%;
  border: 1px solid ${COLOR_MEDIUM_GRAY};
  border-radius: 8px;
  width: 100%;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 100%;
    &:not(:last-child) {
      margin-bottom: 16px;
    }
  `}
`;

const PhoneInputStyled = styled(PhoneInput)`
  border-radius: 8px;
  border: 1px solid ${COLOR_MEDIUM_GRAY};
  width: 100%;
  height: ${56 + 56 * 0.38}px;
  padding: 0 25px;
  background: #fff;
  margin-top: 20px;

  div {
    margin-right: 8px;
  }

  input {
    border: none;
    font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  }

  input:focus-visible {
    outline: none;
  }
`;

const FlexBoxStyled = styled(FlexBox)`
  position: relative;
  width: fit-content;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    `}
`;

const Button = styled.button<{ backgroundColor: string; editable: boolean }>`
  min-height: 10px;
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  font-weight: 500;
  padding: 24px 40px;
  border-radius: 40px;
  text-align: center;
  transition:
    transform 0.2s ease 0s,
    box-shadow 0.2s ease 0s;
  cursor: pointer;
  background-color: ${(props) => props.backgroundColor};
  border: none;
  ${(props) => props.editable && `cursor: text;`}

  ${(props) =>
    props.editable
      ? ''
      : `
  &:hover {
    transform: translateY(-3px);
    box-shadow: rgba(0, 0, 0, 0.2) 0px 1rem 2rem;
  }`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 17px 28px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    &:hover {
      transform: translateY(0px);
      box-shadow: rgba(0, 0, 0, 0) 0px 0rem 0rem;
    }
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
    padding: 25px 10px;
    display: flex;
    justify-content: center;
    align-items: center;
  `}

  ${({ theme: { $width } }) =>
    $width <= 375 &&
    `
    padding: 18px 10px;
  `}

  & p {
    ${(props) => props.editable && `cursor: text !important;`};
    display: inline-block;
  }
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 20px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-top: 15px;
  margin-bottom: 15px;
  & label {
    display: block;
  }
  & span {
    font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
    font-family: inherit !important;
  }
  & span:first {
    font-size: ${({ theme: { $headlineFont } }) => $headlineFont};
  }

  & input {
    width: ${({ theme: { $headlineFont } }) => $headlineFont};
    height: ${({ theme: { $headlineFont } }) => $headlineFont};
  }

  & svg {
    width: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
    height: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  }
`;

const VisibilityToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const UpsellContainer = styled.div`
  border: 1px dashed black;
  padding: 3rem 1rem 3rem 3rem;
  width: 100%;
  background-color: #d5fafc;
  margin-top: 20px;

  &:focus-within {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`;

const UpsellCheckbox = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid black;
  position: relative;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 15px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
`;

export type FirstInputType = {
  phone?: string;
  email: string;
  first_name: string;
  last_name: string;
  subscribe: boolean;
  city: string;
  street: string;
  apartment: string;
  zipcode: string;
  state: string;
  is_vip?: boolean;
};

export type FirstProps = {
  button: SalesValues;
  backgroundColor: string;
  callback: (values: FirstInputType) => void;
  loadStripe?: boolean;
  textColor: BookBaseColor;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: Record<string, any>;
  setIsVip: (isVip: boolean) => void;
  oppositeBackgroundColor?: string | undefined;
  order: number;
};

export const First: FC<FirstProps> = (props) => {
  const {
    backgroundColor,
    callback,
    textColor,
    button,
    meta = {},
    setIsVip,
    oppositeBackgroundColor,
    order
  } = props;

  const {
    salesType,
    togglePhoneInputField,
    salesPageValues,
    amazonURL,
    isShippingForm,
    isSubscription,
    upsellDetails,
    upsellPrice,
    isDashboard
  } = useSales();

  const { editable, updateActiveElement, resetActiveElement } = useWebsiteEditor();
  const [isButtonActionModalOpen, setIsButtonActionmodalOpen] = useState(false);
  const [showLinkIcon, setShowLinkIcon] = useState(false);

  const { t } = useTranslation();

  const [data, setData] = useState({
    phone: '',
    email: '',
    first_name: '',
    last_name: '',
    subscribe: isSubscription,
    city: '',
    street: '',
    apartment: '',
    zipcode: '',
    state: '',
    is_vip: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Accessibility improvements
  const formId = `form-${meta['accessorKey'] || 'signup'}`;
  const emailAccessKey = useMemo(() => generateAccessKey('e', order), [order]);
  const firstNameAccessKey = useMemo(() => generateAccessKey('f', order), [order]);
  const lastNameAccessKey = useMemo(() => generateAccessKey('l', order), [order]);
  const phoneAccessKey = useMemo(() => generateAccessKey('p', order), [order]);
  const submitAccessKey = useMemo(() => generateAccessKey('s', order), [order]);

  const handleFormError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'assertive');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Form error: ${error instanceof Error ? error.message : 'Submission failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 3000);
  };

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'email':
        if (!value) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
      case 'first_name':
        if (!value.trim()) return 'First name is required';
        return '';
      case 'last_name':
        if (!value.trim()) return 'Last name is required';
        return '';
      default:
        return '';
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      // Validate required fields
      const newErrors: Record<string, string> = {};
      Object.keys(data).forEach((key) => {
        if (key === 'first_name' && meta?.['hideFirstname'] && !editable) return;
        if (key === 'last_name' && meta?.['hideLastname'] && !editable) return;

        const error = validateField(key, data[key as keyof typeof data] as string);
        if (error) newErrors[key] = error;
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        // Announce validation errors
        const errorMessage = `Form has ${Object.keys(newErrors).length} error${Object.keys(newErrors).length > 1 ? 's' : ''}. Please check the highlighted fields.`;
        const errorAnnouncement = document.createElement('div');
        errorAnnouncement.setAttribute('aria-live', 'assertive');
        errorAnnouncement.setAttribute('aria-atomic', 'true');
        errorAnnouncement.className = 'sr-only';
        errorAnnouncement.textContent = errorMessage;
        document.body.appendChild(errorAnnouncement);
        setTimeout(() => document.body.removeChild(errorAnnouncement), 3000);
        return;
      }

      const {
        email,
        first_name,
        last_name,
        phone,
        subscribe,
        apartment,
        city,
        street,
        zipcode,
        state
      } = data;

      callback({
        email,
        first_name,
        last_name,
        apartment,
        city,
        street,
        zipcode,
        state,
        ...(data?.is_vip ? { is_vip: data.is_vip } : {}),
        ...(phone && { phone }),
        subscribe
      });

      setErrors({});
    } catch (error) {
      handleFormError(error);
    }
  };

  const handleInputUpdate = (input: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value }
    } = input;
    setData({
      ...data,
      [name]: value
    });
  };

  const handleVisibilityToggle = (fieldName: string) => {
    try {
      if (meta['accessorKey']) {
        togglePhoneInputField(meta['accessorKey'], fieldName);
      }
    } catch (error) {
      handleFormError(error);
    }
  };

  const checkboxColor = useMemo(() => {
    if (tinycolor(backgroundColor).toHex() === 'ffffff') {
      return 'gray';
    }
    return backgroundColor;
  }, [backgroundColor]);

  const buttonBackground = useMemo(() => {
    if (button.color) return button.color;
    if (
      salesPageValues &&
      typeof salesPageValues['topbar_1'] === 'object' &&
      salesPageValues['topbar_1'].button?.color
    ) {
      return salesPageValues['topbar_1'].button?.color;
    }
    return backgroundColor;
  }, [backgroundColor, salesPageValues, button]);

  const color = useMemo(() => {
    return tinycolor(buttonBackground).isDark() ? '#FFFFFF' : '#000000';
  }, [buttonBackground]);

  return (
    <Form onSubmit={handleSubmit} id={formId} role="form" aria-label="Sign up form" noValidate>
      <InputInner>
        {!meta?.['hideFirstname'] || editable ? (
          <FlexBox
            position="relative"
            alignItems="center"
            justifyContent="center"
            width="100%"
            gap={5}>
            {editable && (
              <VisibilityToggle
                type="button"
                onClick={() => handleVisibilityToggle('hideFirstname')}
                aria-label={
                  meta?.['hideFirstname'] ? 'Show first name field' : 'Hide first name field'
                }
                title={meta?.['hideFirstname'] ? 'Show first name field' : 'Hide first name field'}>
                <img
                  src={meta?.['hideFirstname'] ? '/img/eye-hide.svg' : '/img/eye-show.svg'}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: 32,
                    marginRight: 10
                  }}
                />
              </VisibilityToggle>
            )}
            <Input
              name="first_name"
              placeholder={t('common.name', { defaultValue: 'Name' })}
              required
              value={data.first_name}
              style={{ opacity: meta['hideFirstname'] ? 0.5 : 1 }}
              onChange={handleInputUpdate}
              aria-label="First name"
              aria-describedby={errors['first_name'] ? 'first-name-error' : undefined}
              aria-invalid={!!errors['first_name']}
              title={`Press Alt+${firstNameAccessKey} to focus first name field`}
            />
          </FlexBox>
        ) : null}

        {!meta['hideLastname'] || editable ? (
          <FlexBox
            position="relative"
            alignItems="center"
            justifyContent="center"
            width="100%"
            gap={5}>
            {editable && (
              <VisibilityToggle
                type="button"
                onClick={() => handleVisibilityToggle('hideLastname')}
                aria-label={
                  meta?.['hideLastname'] ? 'Show last name field' : 'Hide last name field'
                }
                title={meta?.['hideLastname'] ? 'Show last name field' : 'Hide last name field'}>
                <img
                  src={meta?.['hideLastname'] ? '/img/eye-hide.svg' : '/img/eye-show.svg'}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: 32,
                    marginRight: 10
                  }}
                />
              </VisibilityToggle>
            )}
            <Input
              name="last_name"
              placeholder={t('auth.common.lastName', { defaultValue: 'Last Name' })}
              required
              value={data.last_name}
              style={{ opacity: meta['hideLastname'] ? 0.5 : 1 }}
              onChange={handleInputUpdate}
              aria-label="Last name"
              aria-describedby={errors['last_name'] ? 'last-name-error' : undefined}
              aria-invalid={!!errors['last_name']}
              title={`Press Alt+${lastNameAccessKey} to focus last name field`}
            />
          </FlexBox>
        ) : null}
      </InputInner>

      <FlexBox position="relative" alignItems="center" justifyContent="center" width="100%">
        <InputLarge
          name="email"
          placeholder={t('common.email', { defaultValue: 'Email Address' })}
          required
          value={data.email}
          onChange={handleInputUpdate}
          type="email"
          aria-label="Email address"
          aria-describedby={errors['email'] ? 'email-error' : undefined}
          aria-invalid={!!errors['email']}
          title={`Press Alt+${emailAccessKey} to focus email field`}
        />
      </FlexBox>

      {!meta['hidePhoneInput'] || editable ? (
        <FlexBox position="relative" alignItems="center" justifyContent="center" width="100%">
          {editable && (
            <VisibilityToggle
              type="button"
              onClick={() => handleVisibilityToggle('hidePhoneInput')}
              aria-label={meta['hidePhoneInput'] ? 'Show phone field' : 'Hide phone field'}
              title={meta['hidePhoneInput'] ? 'Show phone field' : 'Hide phone field'}
              style={{
                paddingTop: 25,
                marginRight: 17
              }}>
              <img
                src={meta['hidePhoneInput'] ? '/img/eye-hide.svg' : '/img/eye-show.svg'}
                alt=""
                aria-hidden="true"
                style={{
                  width: 32
                }}
              />
            </VisibilityToggle>
          )}
          <PhoneInputStyled
            defaultCountry="US"
            placeholder={t('auth.common.phoneNumber', {
              defaultValue: 'Phone Number (000)000-0000'
            })}
            value={data.phone}
            onChange={(phone: string) => setData({ ...data, phone })}
            style={{ opacity: meta['hidePhoneInput'] ? 0.5 : 1 }}
            aria-label="Phone number"
            title={`Press Alt+${phoneAccessKey} to focus phone field`}
          />
        </FlexBox>
      ) : null}

      {isShippingForm ? (
        <FlexBox
          flexDirection="column"
          gap={15}
          mt={15}
          width="100%"
          role="group"
          aria-label="Shipping address">
          <InputLarge
            name="street"
            placeholder={'Mailing Street Address'}
            required
            value={data.street}
            onChange={handleInputUpdate}
            aria-label="Street address"
          />
          <InputLarge
            name="apartment"
            placeholder={'Apartments, suites, etc. (optional)'}
            value={data.apartment}
            onChange={handleInputUpdate}
            aria-label="Apartment, suite, etc. (optional)"
          />
          <FlexBox gap={10}>
            <Input
              name="city"
              placeholder={'City'}
              required
              value={data.city}
              onChange={handleInputUpdate}
              aria-label="City"
            />
            <FlexBox>
              <SalesStatesDropdown
                value={data.state}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    state: e.target.value
                  }))
                }
                aria-label="State"
              />
            </FlexBox>
            <Input
              name="zipcode"
              placeholder={'Zip Code'}
              required
              value={data.zipcode}
              onChange={handleInputUpdate}
              aria-label="Zip code"
            />
          </FlexBox>
        </FlexBox>
      ) : null}

      {upsellDetails?.product_name && upsellDetails?.upsell_type === 'bump' ? (
        <UpsellContainer role="group" aria-label="Upsell offer">
          <FlexBox alignItems="center">
            <UpsellCheckbox
              type="button"
              onClick={() => {
                setIsVip(!data.is_vip);
                setData({
                  ...data,
                  is_vip: !data.is_vip
                });
              }}
              style={{ backgroundColor: buttonBackground }}
              aria-pressed={data.is_vip}
              aria-label={`Add ${upsellDetails.product_name} for ${TOP_USED_CURRENCIES?.[upsellDetails?.currency as keyof typeof TOP_USED_CURRENCIES] || upsellDetails?.currency}${upsellPrice}`}>
              {data?.is_vip ? (
                <CheckIcon
                  sx={{
                    fontSize: '30px'
                  }}
                  aria-hidden="true"
                />
              ) : null}
            </UpsellCheckbox>
            <Paragraph>
              BUT WAIT!! Add our {upsellDetails.product_name} for just{' '}
              {TOP_USED_CURRENCIES?.[upsellDetails?.currency as keyof typeof TOP_USED_CURRENCIES] ||
                upsellDetails?.currency}
              {upsellPrice}
            </Paragraph>
          </FlexBox>
        </UpsellContainer>
      ) : null}

      <FlexBox width="98%" pt={3}>
        <StyledFormControlLabel
          style={{ color: filterBackgroundColor(oppositeBackgroundColor || '#fff', textColor) }}
          label={t('components.checkboxSubscribe', {
            defaultValue:
              'I want the good stuff! I want to subscribe to your updates, offers & inbox-worthy content!'
          })}
          control={
            <StyledCheckbox
              checked={data.subscribe}
              onChange={() =>
                setData((prev) => ({
                  ...prev,
                  subscribe: !prev.subscribe
                }))
              }
              aria-required={false}
              aria-label="Subscribe to updates and offers"
              style={{
                color: filterBackgroundColor(backgroundColor, textColor),
                backgroundColor: checkboxColor,
                marginRight: 10
              }}
            />
          }
        />
      </FlexBox>

      <FlexBoxStyled
        onMouseEnter={() => setShowLinkIcon(true)}
        onMouseLeave={() => setShowLinkIcon(isButtonActionModalOpen)}>
        {isButtonActionModalOpen && editable ? (
          <ButtonActionModal
            onClose={() => {
              setIsButtonActionmodalOpen(false);
              setShowLinkIcon(false);
            }}
            meta={meta}
            aria-label="Button configuration modal"
            role="dialog"
            aria-modal="true"
          />
        ) : null}

        {process.env['APP_TYPE'] === 'dashboard' && editable && showLinkIcon ? (
          <ButtonSettingIcon
            setIsButtonActionModalOpen={setIsButtonActionmodalOpen}
            setShowLinkIcon={setShowLinkIcon}
          />
        ) : null}

        <Button
          id={button.id}
          data-anchor-disabled={true}
          backgroundColor={buttonBackground}
          editable={!!isDashboard}
          dangerouslySetInnerHTML={{
            __html: sanitizeRichText(
              button.val,
              editable,
              false,
              salesType === 'sales' && !!amazonURL
                ? `<p>${t('components.completePayment', { defaultValue: 'COMPLETE PAYMENT' })} >></p>`
                : `<p>${t('components.signUp', { defaultValue: 'Sign Up to Get Started' })}</p>`
            )
          }}
          onClick={(e) => {
            if (editable) {
              try {
                setIsButtonActionmodalOpen(true);
                updateActiveElement(e);
              } catch (error) {
                handleFormError(error);
              }
            }
          }}
          onBlur={resetActiveElement}
          data-accessor-parent={meta['accessorKey']}
          data-accessorkey="button"
          style={{ color: color }}
          title={`Press Alt+${submitAccessKey} to ${editable ? 'edit' : 'submit'} this form`}
          aria-label={
            editable
              ? 'Edit submit button'
              : salesType === 'sales' && !!amazonURL
                ? 'Complete payment'
                : 'Sign up to get started'
          }
          {...(isDashboard ? { as: 'p', type: 'button' } : { editable, type: 'submit' })}
        />
      </FlexBoxStyled>
    </Form>
  );
};
